import type { TreeSource } from "@omegagrid/tree";
import type { Accounting } from "@reporting/rp-types/core";

/** the key of a group heading in the company tree; the key of a company is its number */
export const accountingGroupNodeKey = (idGroup: number) => `g-${idGroup}`;

export type AccountingLabel = (a: Accounting) => string;

export const defaultAccountingLabel: AccountingLabel = a => `${a.accounting} ${a.company}`;

/**
 * The companies as a tree by accounting groups: a heading per group and the companies under it in
 * their parent/child hierarchy wherever the parent is listed too - a parent outside the list (another
 * group, hidden, out of the caller's rights) makes the company a root of its group. A heading is never
 * picked itself (`ch: false`); in a multiple-choice tree its level badges pick the companies of the
 * group at once, 1 the companies and 2 their subsidiaries. A lone group stands open, several stand
 * collapsed. The groups come by name, the companies in the given order.
 */
export function buildAccountingTree(accountings: Accounting[], label: AccountingLabel = defaultAccountingLabel): TreeSource {
	const groups = new Map<number, { name: string, items: Accounting[] }>();
	for (const a of accountings) {
		const id = a.id_accounting_group ?? 0;
		if (!groups.has(id)) groups.set(id, { name: a.group_name ?? '', items: [] });
		groups.get(id).items.push(a);
	}
	const sorted = [...groups].sort(([idA, a], [idB, b]) => a.name.localeCompare(b.name, undefined, { numeric: true }) || idA - idB);
	const tree: TreeSource = [];
	for (const [id, group] of sorted) {
		tree.push({ k: accountingGroupNodeKey(id), v: `${id} ${group.name}`, l: 0, ch: false, collapsed: sorted.length > 1 });
		tree.push(...companyNodes(group.items, label));
	}
	return tree;
}

/** The companies of one group: a parent, its subsidiaries one level deeper, the next parent */
function companyNodes(items: Accounting[], label: AccountingLabel): TreeSource {
	const listed = new Set(items.map(a => a.accounting));
	const children = new Map<number|null, Accounting[]>();
	for (const a of items) {
		const parent = a.accounting_parent != null && a.accounting_parent !== a.accounting && listed.has(a.accounting_parent)
			? a.accounting_parent : null;
		if (!children.has(parent)) children.set(parent, []);
		children.get(parent).push(a);
	}

	const nodes: TreeSource = [];
	const done = new Set<number>();
	const walk = (parent: number|null, level: number) => {
		for (const a of children.get(parent) ?? []) {
			if (done.has(a.accounting)) continue;
			done.add(a.accounting);
			nodes.push({ k: String(a.accounting), v: label(a), l: level });
			walk(a.accounting, level + 1);
		}
	};
	walk(null, 1);
	// parents in a cycle reach no root: each is listed where it is met first, at the level of the group
	for (const a of items) {
		if (done.has(a.accounting)) continue;
		done.add(a.accounting);
		nodes.push({ k: String(a.accounting), v: label(a), l: 1 });
		walk(a.accounting, 2);
	}
	return nodes;
}
